import { Box, Editor, TLShape, TLShapeId } from "tldraw";
import { ET_TYPESET_SHAPE_TYPES } from "@ever-tutor/et-ui";
import { isAnnotationShape } from "../utils/annotationContent";

/**
 * Module contract
 *
 * Owns: the single bitmap that stands in for every non-annotation `math` /
 * `rich-text` shape on the page, and the rule deciding whether it may be
 * reused as is, patched, or thrown away.
 *
 * Why one bitmap and not one per shape: a per-shape crop needs to know where a
 * formula ends, KaTeX overflows its own box by an amount that depends on the
 * content, and every attempt to measure that was wrong. A whole-layer bitmap is
 * cut only by the viewport, which is a rectangle already known exactly.
 *
 * tldraw records are immutable, so identity of the ordered shape list is an
 * exact content signature — an edit, a move or a restack all produce a
 * different record, and all of those are rebuilds.
 *
 * Adding and removing are not, and the asymmetry is worth stating. A shape
 * added can simply be painted over the bitmap. A shape removed has to have its
 * pixels erased, and the only honest way to know which those are is to have
 * watched it arrive: when a shape is drawn by itself, the export's own alpha
 * says exactly what it covered, and that rect is kept. A shape whose rect was
 * never measured — everything present at the last rebuild — forces a rebuild
 * when it goes, because guessing where it ended is precisely the mistake this
 * design exists to avoid, and a guess that falls short leaves a fragment of a
 * deleted equation on the page for good.
 *
 * Annotation shapes are NOT cached. `ScreenshotPartUtil` drops annotations
 * that collide with the student's ink, so their membership flips with every
 * stroke; a key built over them would be invalidated constantly. The split is
 * by meaning rather than by type: the problem statement is cached, the marks
 * over the student's work are drawn live.
 *
 * Holds no editor reference and no timers; the entry dies with the agent.
 */

/**
 * Past this the bitmap costs more memory than the render costs time (~16 MB of
 * canvas at 4 bytes/px). A real board's typeset shapes occupy roughly 1000x900.
 * honest miss beats unpredictable memory, so we decline rather than downscale.
 */
const MAX_LAYER_AREA_PX = 4_000_000;

/**
 * How far a typeset shape may paint outside its own box, for shapes whose real
 * extent was never measured.
 *
 * `et-ui/style.css` hides KaTeX's struts, so the `.katex` box collapses toward
 * the line box while glyph spans overflow above and below by an amount that
 * grows with the content's depth — and with the type size, which is why this
 * scales with `fontSize` rather than being a flat number of pixels.
 *
 * Not a measurement, and it does not pretend to be one. But the two directions
 * are not equally survivable, so it errs generous: too much costs a rebuild we
 * would likely have done anyway, while too little lets a kept formula go
 * unrepainted and be occluded by the one painted over it. It is never used to
 * decide what to ERASE — see the module contract.
 */
const EXTENT_SLACK_EM = 2.5;
const EXTENT_SLACK_MIN_PX = 32;
/** Matches `MathShapeUtil`'s default, for a record that carries no size. */
const ASSUMED_FONT_SIZE_PX = 26;

function typesetSlackPx(shape: TLShape): number {
  const fontSize =
    (shape.props as { fontSize?: number } | undefined)?.fontSize ??
    ASSUMED_FONT_SIZE_PX;
  return Math.max(EXTENT_SLACK_MIN_PX, fontSize * EXTENT_SLACK_EM);
}

/**
 * Whether this shape is the page's own, rather than a group's or a frame's.
 *
 * The layer may only hold top-level shapes, and the rule is structural rather
 * than cautious. tldraw exports the ids it is given PLUS their descendants, so
 * a formula inside a group would be drawn once by the content pass (as part of
 * its ancestor) and again by this layer. Left in `content` it is drawn exactly
 * once, at the cost of the foreignObject — correct, and rare enough not to
 * matter. Grouping is reachable: the default UI still binds cmd/ctrl+G, and an
 * agent-created formula is unlocked once complete.
 */
export function isTopLevelTypeset(shape: TLShape): boolean {
  return String(shape.parentId).startsWith("page:");
}

export interface TypesetLayerEntry {
  /** The exact records the bitmap was rendered from, in page order. */
  shapes: TLShape[];
  /** Page-space rect the bitmap covers. */
  bounds: Box;
  /** Device px per page unit the bitmap was rendered at. */
  scale: number;
  canvas: HTMLCanvasElement;
  /**
   * Page-space rect of what a shape actually put on the bitmap, for the shapes
   * drawn in a pass of their own. A batch drawn together shares one rect: it
   * over-states each member, which only ever costs an extra redraw.
   */
  paintedRects: Map<TLShapeId, Box>;
}

/**
 * What to do about the typeset layer this press.
 *
 * `patch` carries the bitmap to build on rather than a promise to mutate the
 * cached one: a pass that fails halfway must leave the entry exactly as it was,
 * not half-painted. Both its fields are optional in effect — an append has
 * nothing to erase, a lone deletion has nothing to redraw and costs no export
 * at all.
 */
export type TypesetLayerPlan =
  | { kind: "reuse"; canvas: HTMLCanvasElement; bounds: Box }
  | {
      kind: "patch";
      base: HTMLCanvasElement;
      baseBounds: Box;
      erase: Box | null;
      redraw: TLShape[];
    }
  | { kind: "rebuild" };

/** The result of one pass, handed back so the entry can record what it knows. */
export interface TypesetLayerCommit {
  bounds: Box;
  canvas: HTMLCanvasElement;
  /** Shapes drawn in this pass; empty for a pure erase. */
  drawnShapes: TLShape[];
  /** What those shapes covered, from the export's alpha; null if they drew nothing. */
  drawnRect: Box | null;
}

/**
 * What `rasterizeWithBlackInk` needs to draw the typeset layer without knowing
 * where the bitmap is kept or how long it lives.
 */
export interface TypesetLayerCache {
  /** Every shape the layer must end up containing, in page order. */
  shapes: TLShape[];
  /** Page-space rect a rebuilt or patched bitmap covers. */
  bounds: Box;
  plan(): TypesetLayerPlan;
  commit(result: TypesetLayerCommit): void;
}

/** One entry per agent; an agent that goes away takes its bitmap with it. */
const layers = new WeakMap<object, TypesetLayerEntry>();

/**
 * Every typeset shape eligible for the cached layer, in page order.
 *
 * Deliberately page-wide rather than limited to the requested bounds: built
 * from the visible set, a camera move would change the list and throw the
 * bitmap away on every pan.
 */
export function collectCacheableTypeset(editor: Editor): TLShape[] {
  return editor
    .getCurrentPageShapesSorted()
    .filter(
      (shape) =>
        ET_TYPESET_SHAPE_TYPES.has(shape.type) &&
        isTopLevelTypeset(shape) &&
        !shape.meta?.hideFromAgent &&
        !isAnnotationShape(shape),
    );
}

/**
 * Widen a rect until both its edges land on whole device pixels.
 *
 * The patch path redraws the cached bitmap into a new canvas every time the
 * layer moves or grows. Snapped, that redraw is an integer translation and
 * copies pixels verbatim; unsnapped it is a fractional one, which resamples —
 * and the blur compounds, once per patched press, over a lesson's worth of
 * turns.
 */
export function snapTypesetLayerBounds(bounds: Box, scale: number): Box {
  const minX = Math.floor(bounds.minX * scale) / scale;
  const minY = Math.floor(bounds.minY * scale) / scale;
  const maxX = Math.ceil(bounds.maxX * scale) / scale;
  const maxY = Math.ceil(bounds.maxY * scale) / scale;
  return new Box(minX, minY, maxX - minX, maxY - minY);
}

/**
 * Device size of a bitmap covering `bounds`, in exactly tldraw's arithmetic.
 *
 * ONE convention, because the alternative was two. tldraw rasterizes an export
 * at `Math.floor(bounds.w * scale * pixelRatio)` (getSvgAsImage), and rounding
 * instead disagrees with it far more often than the snapping suggests: the
 * snapped bounds are whole device pixels in exact arithmetic only, and after a
 * divide and a multiply back a width lands on 889.9999999999999 about one time
 * in six at a downscaled `scale`. Floor says 889, round says 890, and a
 * one-pixel difference between the canvas the cache holds and the canvas a
 * patch builds is a seam that moves every press.
 */
export function typesetLayerCanvasSize(
  bounds: Box,
  scale: number,
): { width_px: number; height_px: number } {
  return {
    width_px: Math.max(1, Math.floor(bounds.w * scale)),
    height_px: Math.max(1, Math.floor(bounds.h * scale)),
  };
}

/**
 * The rect the bitmap must cover: the shapes' masked page bounds plus the slack
 * KaTeX paints outside them. Null if none of them has bounds at all.
 *
 * The slack is not decoration. The export is clipped to exactly these bounds
 * (`padding: 0`), and a formula in the middle of the layer has its overflow
 * covered by a neighbour's box while the outermost one has nothing beyond it —
 * so without this, the edge formula is shaved and the crop is then cached.
 * Drawn over the whole screenshot frame, as before this layer existed, the
 * margin came free.
 */
export function typesetLayerBounds(
  editor: Editor,
  shapes: TLShape[],
): Box | null {
  let box: Box | null = null;
  for (const shape of shapes) {
    const bounds = editor.getShapeMaskedPageBounds(shape);
    if (!bounds) continue;
    const padded = Box.ExpandBy(bounds, typesetSlackPx(shape));
    box = box ? Box.Expand(box, padded) : padded;
  }
  return box;
}

/**
 * A rect big enough to hold what a shape paints, for shapes never measured.
 *
 * An unmeasurable shape gets a rect covering the whole page, so it is treated
 * as touching everything: unknown paint order must fall to the rebuild, not to
 * a guess. Bounds are read once per shape per press.
 */
export function typesetShapeExtent(editor: Editor): (shape: TLShape) => Box {
  const boxes = new Map<TLShape, Box>();
  const everything = new Box(-1e6, -1e6, 2e6, 2e6);
  return (shape) => {
    let box = boxes.get(shape);
    if (!box) {
      const bounds = editor.getShapeMaskedPageBounds(shape);
      box = bounds ? Box.ExpandBy(bounds, typesetSlackPx(shape)) : everything;
      boxes.set(shape, box);
    }
    return box;
  };
}

export type TypesetLayerDiff =
  | { kind: "reuse" }
  | { kind: "patch"; erase: Box | null; redraw: TLShape[] }
  | { kind: "rebuild" };

/** The two geometric questions the rule asks, kept separate on purpose. */
export interface TypesetLayerGeometry {
  /** Exactly what this shape put on the bitmap, or null if never measured. */
  paintedRectOf(shape: TLShape): Box | null;
  /** A rect big enough to hold what the shape paints; a guess when unmeasured. */
  extentOf(shape: TLShape): Box;
}

/**
 * How `current` differs from what the bitmap already holds.
 *
 * Input: `cached` and `current` in page order, and the geometry above.
 * Output: `reuse` for an identical list; `patch` with a rect to clear and a set
 *   to repaint; `rebuild` when neither is safe or neither is cheaper.
 *
 * Takes geometry as an argument rather than an editor so the rule can be tested
 * without a DOM — it is the half that fails silently.
 */
export function diffTypesetLayer(
  cached: TLShape[],
  current: TLShape[],
  geometry: TypesetLayerGeometry,
): TypesetLayerDiff {
  const currentSet = new Set(current);
  const cachedSet = new Set(cached);
  const removed = cached.filter((shape) => !currentSet.has(shape));
  const kept = cached.filter((shape) => currentSet.has(shape));
  const redraw = new Set(current.filter((shape) => !cachedSet.has(shape)));

  // Everything still on the page has to still be in the same relative order,
  // or the pixels already on the bitmap are stacked wrongly and no amount of
  // painting over them fixes it. A restack fails here.
  let matched = 0;
  for (const shape of current) {
    if (matched < kept.length && kept[matched] === shape) matched++;
  }
  if (matched !== kept.length) return { kind: "rebuild" };
  if (removed.length === 0 && redraw.size === 0) return { kind: "reuse" };

  let erase: Box | null = null;
  for (const shape of removed) {
    const rect = geometry.paintedRectOf(shape);
    // Never drawn alone, so nothing here knows where it ended. Guessing short
    // would leave a fragment of a deleted equation on the layer permanently.
    if (!rect) return { kind: "rebuild" };
    erase = erase ? Box.Expand(erase, rect) : rect.clone();
  }

  // Clearing the rect takes out whatever else was painted inside it.
  if (erase) {
    for (const shape of kept) {
      if (geometry.extentOf(shape).collides(erase)) redraw.add(shape);
    }
  }

  // Repainted pixels land on top of everything else on the bitmap, so anything
  // a repainted shape covers has to be repainted too — and only shapes ABOVE
  // it in paint order (`j > i`), because the ones below were already under it
  // and stay there. Runs to a fixed point: one shape pulling in its neighbour
  // can pull in that one's neighbour, so a newly added shape goes back on the
  // worklist. Each pair is tested at most once.
  const pending: number[] = [];
  for (let i = 0; i < current.length; i++) {
    if (redraw.has(current[i])) pending.push(i);
  }
  while (pending.length > 0) {
    const i = pending.pop() as number;
    const below = geometry.extentOf(current[i]);
    for (let j = i + 1; j < current.length; j++) {
      if (redraw.has(current[j])) continue;
      if (!below.collides(geometry.extentOf(current[j]))) continue;
      redraw.add(current[j]);
      pending.push(j);
    }
  }

  // Repainting all of them IS the rebuild, minus the saving and plus a canvas.
  if (redraw.size === current.length) return { kind: "rebuild" };
  return {
    kind: "patch",
    erase,
    redraw: current.filter((shape) => redraw.has(shape)),
  };
}

/**
 * The cache handle for this press, or null when caching would not pay:
 * nothing to cache, no bounds, or a layer too large to hold.
 *
 * Input: `owner` — the agent the bitmap belongs to; `extentOf` — see
 *   `typesetShapeExtent`.
 * Output: a handle, or null to render them live as before.
 */
export function typesetLayerCacheFor(
  owner: object,
  shapes: TLShape[],
  bounds: Box | null,
  scale: number,
  extentOf: (shape: TLShape) => Box,
): TypesetLayerCache | null {
  if (shapes.length === 0 || !bounds) return null;
  const snapped = snapTypesetLayerBounds(bounds, scale);
  if (snapped.w * scale * (snapped.h * scale) > MAX_LAYER_AREA_PX) return null;

  // Memoised because both the caller's log line and the compositor ask, and
  // because the answer must not change between the two.
  let decided: TypesetLayerPlan | null = null;

  return {
    shapes,
    bounds: snapped,
    plan() {
      if (decided) return decided;
      decided = decide();
      return decided;
    },
    commit({ bounds: rendered, canvas, drawnShapes, drawnRect }) {
      const previous = layers.get(owner);
      const carried =
        decided?.kind === "patch" && previous
          ? new Map(previous.paintedRects)
          : new Map<TLShapeId, Box>();
      const alive = new Set(shapes.map((shape) => shape.id));
      for (const id of [...carried.keys()]) {
        if (!alive.has(id)) carried.delete(id);
      }
      // A batch shares one rect. It over-states each member, which costs an
      // extra redraw and never an erasure that falls short.
      if (drawnRect) {
        for (const shape of drawnShapes) carried.set(shape.id, drawnRect);
      } else {
        for (const shape of drawnShapes) carried.delete(shape.id);
      }
      layers.set(owner, {
        shapes,
        bounds: rendered,
        scale,
        canvas,
        paintedRects: carried,
      });
    },
  };

  function decide(): TypesetLayerPlan {
    const entry = layers.get(owner);
    if (!entry || entry.scale !== scale) return { kind: "rebuild" };
    const diff = diffTypesetLayer(entry.shapes, shapes, {
      paintedRectOf: (shape) => entry.paintedRects.get(shape.id) ?? null,
      // A measured rect is also the best extent there is.
      extentOf: (shape) => entry.paintedRects.get(shape.id) ?? extentOf(shape),
    });
    if (diff.kind === "reuse") {
      return { kind: "reuse", canvas: entry.canvas, bounds: entry.bounds };
    }
    if (diff.kind === "patch") {
      return {
        kind: "patch",
        base: entry.canvas,
        baseBounds: entry.bounds,
        erase: diff.erase,
        redraw: diff.redraw,
      };
    }
    return { kind: "rebuild" };
  }
}

/** Source and destination rectangles for a 9-argument `drawImage`. */
export interface TypesetLayerBlit {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

/**
 * Map the part of the cached layer the screenshot frame needs onto that frame.
 *
 * Both rectangles are derived from the page-space intersection rather than from
 * `scale`, so the two roundings that produced `source_*_px` and `target_*_px`
 * cannot drift the typeset layer away from the ink drawn beside them.
 *
 * Returns null when the layer does not reach the frame at all — the caller must
 * not call `drawImage` with a zero-width source, which is a DOMException.
 */
export function typesetLayerBlit(args: {
  source_bounds: Box;
  source_width_px: number;
  source_height_px: number;
  target_bounds: Box;
  target_width_px: number;
  target_height_px: number;
}): TypesetLayerBlit | null {
  const { source_bounds: source, target_bounds: target } = args;

  const left = Math.max(source.minX, target.minX);
  const top = Math.max(source.minY, target.minY);
  const right = Math.min(source.maxX, target.maxX);
  const bottom = Math.min(source.maxY, target.maxY);
  if (right <= left || bottom <= top) return null;

  const sourceScaleX = args.source_width_px / source.w;
  const sourceScaleY = args.source_height_px / source.h;
  const targetScaleX = args.target_width_px / target.w;
  const targetScaleY = args.target_height_px / target.h;

  return {
    sx: (left - source.minX) * sourceScaleX,
    sy: (top - source.minY) * sourceScaleY,
    sw: (right - left) * sourceScaleX,
    sh: (bottom - top) * sourceScaleY,
    dx: (left - target.minX) * targetScaleX,
    dy: (top - target.minY) * targetScaleY,
    dw: (right - left) * targetScaleX,
    dh: (bottom - top) * targetScaleY,
  };
}
